import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Components
import Button, { BUTTON_STYLES } from '../button/button';
import ActionList from './action-list';

// Helpers
import { ESCAPE_KEY_CODE, ESCAPE_KEY_NAME } from '../../helpers/enums';
import PortalCommunication from '../../helpers/portal-communications';
import Resources from '../../helpers/resources';

// Hooks
import useOutsideClick from '../../hooks/use-outside-click';
import useKeyPress from '../../hooks/use-key-press';
import useScreenMode from '../../hooks/use-screen-mode';

function Tools({ parentClass, classesLength, studentsLength, currentClassName, currentClassId, exportStudents, isSisSchool }) {
    const { i18n } = useLocalisation();
    const [showActions, setShowActions] = useState(false);
    // Configuration item for the info modal
    const [girlIds, setGirlIds] = useState([]);
    const actionRef = useRef(null);
    const actionButtonRef = useRef(null);

    // Initialise the necessary information if escape key has been pressed
    const [escapeKeyPressed] = useKeyPress({
        targetKeyName: ESCAPE_KEY_NAME,
        targetKeyCode: ESCAPE_KEY_CODE
    });

    // ComponentDidMount
    useEffect(() => {
        // retrieve the value for girl ids for the info modal
        const retrieveConfigItems = async () => {
            const infoModalIds = await Resources.getConfigurationItem('InfoModalGiRLids');
            if (Array.isArray(infoModalIds)) {
                setGirlIds(infoModalIds);
            } else if (typeof infoModalIds === 'string') {
                try {
                    const parsed = JSON.parse(infoModalIds);
                    setGirlIds(Array.isArray(parsed) ? parsed : []);
                } catch (error) {
                    setGirlIds([]);
                }
            } else {
                setGirlIds([]);
            }
        };

        retrieveConfigItems();
    }, []);

    // Handler
    const toggleActionVisibilityHandler = () => {
        setShowActions(!showActions);
    };

    // Hook to check if the element has been clicked outside
    useOutsideClick(
        [actionButtonRef, actionRef],
        toggleActionVisibilityHandler
    );

    // Effect for handling escape key press
    useEffect(() => {
        // Only act on the escapeKey pressed when showActions is also set to true
        if (escapeKeyPressed && showActions && toggleActionVisibilityHandler) {
            toggleActionVisibilityHandler();
        }
    }, [escapeKeyPressed]);

    // Hook to make the tools responsive
    const [screenMode] = useScreenMode();

    const buttonMode = screenMode.includes('extendedLaptop') ? BUTTON_STYLES.iconText : BUTTON_STYLES.iconSVG;

    const howDoesItWorkHandler = () => {
        PortalCommunication.requestInfoModal(girlIds);
    };

    return (
        <div className={parentClass}>
            { girlIds.length > 0 ?
                <Button
                    buttonStyle={buttonMode}
                    visibleText={<Trans id={'Tools - how does this work?'} />}
                    importedClasses="button--secondary"
                    iconName="infocircle"
                    ariaProps={{ 'aria-label': i18n._(t`Tools - how does this work?`) }}
                    isBtnDisabled={girlIds.length === 0}
                    callback={howDoesItWorkHandler}
                /> : '' }
            <Button
                ref={actionButtonRef}
                buttonStyle={buttonMode}
                visibleText={<Trans id={'Tools - Actions'} />}
                iconName="actions"
                ariaProps={
                    {
                        role: 'switch',
                        'aria-label': i18n._(t`Tools - Actions`),
                        'aria-checked': showActions
                    }
                }
                isBtnDisabled={classesLength === 0}
                importedClasses="button--actions button--expanded"
                callback={toggleActionVisibilityHandler}
            />
            {
                showActions ? (
                    <ActionList
                        ref={actionRef}
                        studentsLength={studentsLength}
                        currentClassName={currentClassName}
                        currentClassId={currentClassId}
                        closeActionList={() => setShowActions(false)}
                        exportStudents={exportStudents}
                        isSisSchool={isSisSchool}
                    />
                ) : null
            }
        </div>
    );
}

Tools.defaultProps = {
    classesLength: 0,
    studentsLength: 0
};

Tools.propTypes = {
    parentClass: PropTypes.string,
    classesLength: PropTypes.number,
    studentsLength: PropTypes.number,
    currentClassName: PropTypes.string,
    currentClassId: PropTypes.number,
    exportStudents: PropTypes.func,
    isSisSchool: PropTypes.bool
};

export default Tools;



// WEBPACK FOOTER //
// ./components/tools/tools.js